import { lazy, Suspense, useCallback, useEffect, useId, useState } from "react";
import { AppHeader } from "./components/AppHeader";
import { EditorPanel } from "./components/editor/EditorPanel";
import { Icon } from "./components/ui/Icon";
import { initializeDraftPersistence } from "./store/skinStore";
import { cx } from "./styles/cx";
import panel from "./styles/panel.module.css";
import styles from "./App.module.css";

const PreviewPanel = lazy(() =>
  import("./components/PreviewPanel").then((module) => ({
    default: module.PreviewPanel,
  })),
);
const AIPanel = lazy(() =>
  import("./components/ai").then((module) => ({ default: module.AIPanel })),
);

type MobileTab = "draw" | "preview" | "ai";
const MOBILE_TAB_ORDER: readonly MobileTab[] = ["draw", "preview", "ai"];

interface ToastMessage {
  id: string;
  message: string;
  tone: "success" | "error";
}

function PanelLoading({
  label,
  mobileActive = false,
}: {
  label: string;
  mobileActive?: boolean;
}) {
  return (
    <section
      className={cx(
        panel.workspacePanel,
        panel.panelLoading,
        mobileActive && panel.mobileActive,
      )}
      aria-label={label}
      aria-busy="true"
    >
      <div className={panel.loadingPixels} aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
      </div>
      <strong>{label}</strong>
    </section>
  );
}

export function App() {
  const instanceId = useId();
  const [mobileTab, setMobileTab] = useState<MobileTab>("draw");
  const [tabletAiOpen, setTabletAiOpen] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const notify = useCallback(
    (message: string, tone: "success" | "error" = "success") => {
      const id = `${instanceId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setToasts((current) => [...current.slice(-2), { id, message, tone }]);
      window.setTimeout(
        () =>
          setToasts((current) => current.filter((toast) => toast.id !== id)),
        3200,
      );
    },
    [instanceId],
  );

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setTabletAiOpen(false);
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, []);

  useEffect(() => {
    let disposed = false;
    let stopAutosave: (() => void) | undefined;
    void initializeDraftPersistence()
      .then((cleanup) => {
        if (disposed) cleanup();
        else stopAutosave = cleanup;
      })
      .catch((error: unknown) => {
        notify(
          error instanceof Error ? error.message : "无法恢复本机草稿",
          "error",
        );
      });
    return () => {
      disposed = true;
      stopAutosave?.();
    };
  }, [notify]);

  return (
    <div
      className={styles.appShell}
      {...(tabletAiOpen ? { "data-ai-open": "true" } : {})}
    >
      <AppHeader onNotify={notify} />

      <main className={styles.workspace} id="main-workspace">
        <EditorPanel mobileActive={mobileTab === "draw"} />
        <Suspense
          fallback={
            <PanelLoading
              label="正在装配 3D 试衣间…"
              mobileActive={mobileTab === "preview"}
            />
          }
        >
          <PreviewPanel mobileActive={mobileTab === "preview"} />
        </Suspense>
        <Suspense
          fallback={
            <PanelLoading
              label="正在唤醒像素设计搭档…"
              mobileActive={mobileTab === "ai"}
            />
          }
        >
          <AIPanel mobileActive={mobileTab === "ai"} />
        </Suspense>
      </main>

      <button
        className={styles.tabletAiToggle}
        type="button"
        aria-label={tabletAiOpen ? "关闭 AI 设计搭档" : "打开 AI 设计搭档"}
        aria-expanded={tabletAiOpen}
        onClick={() => setTabletAiOpen((open) => !open)}
      >
        <Icon name={tabletAiOpen ? "chevronRight" : "sparkles"} />
      </button>

      <nav className={styles.mobileTabs} aria-label="主功能" role="tablist">
        <MobileTabButton
          current={mobileTab}
          icon="pencil"
          label="绘制"
          tab="draw"
          onSelect={setMobileTab}
        />
        <MobileTabButton
          current={mobileTab}
          icon="rotate"
          label="3D"
          tab="preview"
          onSelect={setMobileTab}
        />
        <MobileTabButton
          current={mobileTab}
          icon="sparkles"
          label="AI"
          tab="ai"
          onSelect={setMobileTab}
        />
      </nav>

      <div
        className={styles.toastRegion}
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cx(
              styles.toast,
              toast.tone === "success" && styles.success,
              toast.tone === "error" && styles.error,
            )}
            role="status"
          >
            <Icon
              name={toast.tone === "success" ? "check" : "stop"}
              size={16}
            />
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
}

function MobileTabButton({
  current,
  icon,
  label,
  tab,
  onSelect,
}: {
  current: MobileTab;
  icon: "pencil" | "rotate" | "sparkles";
  label: string;
  tab: MobileTab;
  onSelect: (tab: MobileTab) => void;
}) {
  const selectFromKeyboard = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    let nextIndex: number | undefined;
    const currentIndex = MOBILE_TAB_ORDER.indexOf(tab);
    if (event.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % MOBILE_TAB_ORDER.length;
    } else if (event.key === "ArrowLeft") {
      nextIndex =
        (currentIndex - 1 + MOBILE_TAB_ORDER.length) % MOBILE_TAB_ORDER.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = MOBILE_TAB_ORDER.length - 1;
    }
    if (nextIndex === undefined) return;
    event.preventDefault();
    const nextTab = MOBILE_TAB_ORDER[nextIndex];
    onSelect(nextTab);
    requestAnimationFrame(() =>
      document.querySelector<HTMLElement>(`#mobile-tab-${nextTab}`)?.focus(),
    );
  };

  return (
    <button
      id={`mobile-tab-${tab}`}
      className={styles.tabButton}
      type="button"
      role="tab"
      aria-controls={`panel-${tab}`}
      aria-selected={current === tab}
      tabIndex={current === tab ? 0 : -1}
      onKeyDown={selectFromKeyboard}
      onClick={() => onSelect(tab)}
    >
      <Icon name={icon} />
      {label}
    </button>
  );
}
